import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, collection, addDoc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { statoPiano } from "../utils/pianiNexiora";

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

// PLATFORM OWNER UID — imposta il tuo UID qui dopo il primo login
export const PLATFORM_OWNER_UID = "iA7X0toritaWxIJZdbz1IByibnF3";

// Organizzazione demo pubblica — stesso id fisso usato dallo script popola_demo_nexiora.js
export const DEMO_ORG_ID = "demo-nexiora-org";

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  // Registrazione dell'azienda (W446, regole 73): tutto sul server. La Cloud Function
  // registraAzienda controlla la P.IVA (formato, registro delle P.IVA già su Nexiora, VIES) e
  // crea account, azienda, registro e profilo del titolare insieme, o niente. Poi si entra.
  // Il browser non può più creare aziende né profili da titolare (le Regole 73 lo vietano).
  async function registerOrganization(dati) {
    try {
      await httpsCallable(getFunctions(), "registraAzienda")(dati);
    } catch (err) {
      const e = new Error(err?.message || "REGISTRAZIONE_INCOMPLETA");
      e.codice = err?.details?.codice || (err?.code === "functions/resource-exhausted" ? "limite" : "");
      throw e;
    }
    return signInWithEmailAndPassword(auth, dati.email.trim().toLowerCase(), dati.password);
  }

  // Prima della registrazione: la P.IVA è libera? Il server risponde solo «libera / già
  // registrata / non valida» (mai il nome dell'azienda che la usa) e propone i dati del VIES.
  async function verificaPivaRegistrazione(piva) {
    const { data } = await httpsCallable(getFunctions(), "verificaPivaRegistrazione")({ piva });
    return data;
  }

  // P.IVA già su Nexiora: «Chiedi l'accesso» (agli amministratori di quell'azienda) o «È la mia
  // azienda» (a Nexiora). La risposta è sempre la stessa, qualunque sia la P.IVA.
  async function richiediAccesso(dati) {
    await httpsCallable(getFunctions(), "richiediAccesso")(dati);
  }

  // Verifica dell'email (aziende registrate dal W446): serve per invitare e, dopo 7 giorni, per scrivere.
  const [emailVerificata, setEmailVerificata] = useState(false);
  async function aggiornaVerificaEmail() {
    const u = auth.currentUser;
    if (!u) return false;
    try {
      await u.reload();
      if (auth.currentUser?.emailVerified) await auth.currentUser.getIdToken(true); // le Regole leggono il token aggiornato
    } catch { /* senza rete resta lo stato di prima */ }
    const v = !!auth.currentUser?.emailVerified;
    setEmailVerificata(v);
    return v;
  }
  async function rimandaVerificaEmail() {
    const { data } = await httpsCallable(getFunctions(), "inviaVerificaEmail")({});
    if (data?.giaVerificata) await aggiornaVerificaEmail();
    return data;
  }

  // Registrazione di un collega invitato (W445, regole 72). Si entra SOLO con un invito: il
  // browser crea l'account e poi la Cloud Function accettaInvito convalida l'invito (stato,
  // scadenza, email uguale a quella dell'account) e crea il profilo. La vecchia strada con
  // l'ID dell'azienda come «codice» è chiusa: chiunque lo conoscesse poteva agganciarsi.
  async function registerMember(email, password, displayName, codice, phone = "", accettazione = null) {
    const emailNorm = email.trim().toLowerCase();
    const codicePulito = String(codice || "").trim();
    if (!codicePulito || codicePulito.includes("/")) throw new Error("CODICE_NON_VALIDO");
    // Controllo preliminare (l'invito si legge senza account): errori chiari prima di creare
    // l'account. Il controllo che conta lo rifà comunque il server.
    const invitoSnap = await getDoc(doc(db, "inviti", codicePulito));
    if (!invitoSnap.exists()) throw new Error("CODICE_NON_VALIDO");
    const inv = invitoSnap.data();
    if (inv.stato === "revocato") throw new Error("INVITO_REVOCATO");
    if (inv.stato === "accettato") throw new Error("INVITO_GIA_USATO");
    const scaduto = inv.scadenza && (inv.scadenza.toDate ? inv.scadenza.toDate() : new Date(inv.scadenza)) < new Date();
    if (scaduto) throw new Error("INVITO_SCADUTO");
    if (inv.emailDestinatario && inv.emailDestinatario !== emailNorm) throw new Error("INVITO_EMAIL_NON_CORRISPONDENTE");

    const result = await createUserWithEmailAndPassword(auth, emailNorm, password);
    let esito = null;
    try {
      await updateProfile(result.user, { displayName });
      const { data } = await httpsCallable(getFunctions(), "accettaInvito")({
        invitoId: codicePulito, displayName, phone, versionePrivacy: accettazione?.versione || "",
      });
      esito = data || {};
    } catch (err) {
      // Account appena creato ma invito non accettato: si toglie, così la persona può riprovare
      try { await result.user.delete(); } catch { /* resta comunque l'errore chiaro */ }
      if (err?.details?.codice === "scaduto") throw new Error("INVITO_SCADUTO");
      if (err?.details?.codice === "gia-usato") throw new Error("INVITO_GIA_USATO");
      if (err?.details?.codice === "revocato") throw new Error("INVITO_REVOCATO");
      if (err?.details?.codice === "email-diversa") throw new Error("INVITO_EMAIL_NON_CORRISPONDENTE");
      throw new Error("REGISTRAZIONE_INCOMPLETA");
    }
    // Con un ruolo scelto da chi ha invitato si entra subito; un vecchio invito senza ruolo
    // resta in attesa che l'amministratore scelga il ruolo.
    result.accessoImmediato = esito?.abilitato === true;
    if (!result.accessoImmediato) await signOut(auth);
    else await fetchUserProfile(result.user.uid);
    return result;
  }

  // Crea un invito tracciato — solo Titolare/Admin/Gestione/Amministrazione della propria
  // organizzazione (verificato anche lato Regole Firestore, non solo qui). Il documento
  // stesso (il suo ID, generato da Firestore) è il "codice" da condividere con la persona:
  // niente più un ID organizzazione condiviso a voce, ora un token dedicato con scadenza,
  // stato, e legato a un'unica email destinataria.
  async function creaInvito({ email, ruoloPrevisto = null, giorniValidita = 7, diventaTitolare = false, organizationId = null, organizationName = "" }) {
    const scadenza = new Date(Date.now() + giorniValidita * 24 * 60 * 60 * 1000);
    // organizationId va sempre passato esplicitamente da chi chiama questa funzione — MAI
    // dedotto da userProfile.organizationId, che riflette l'organizzazione personale di chi
    // è loggato, non necessariamente quella in cui si trova (es. Platform Owner che ha usato
    // "Entra" per lavorare dentro un'organizzazione diversa dalla propria). Bug reale
    // trovato oggi: senza questo parametro esplicito, un invito creato da Platform Owner
    // mentre è "dentro" un'altra organizzazione finiva nell'organizzazione sbagliata (o
    // veniva rifiutato dalle regole, a seconda dei casi).
    const orgIdFinale = organizationId || userProfile?.organizationId;
    const ref = await addDoc(collection(db, "inviti"), {
      organizationId: orgIdFinale,
      // W445: il nome dell'azienda nell'invito, così la pagina del link mostra dove si entra
      organizationName: organizationName || (orgIdFinale === userProfile?.organizationId ? (organization?.name || organization?.nome || "") : ""),
      emailDestinatario: email.trim().toLowerCase(),
      invitatoDa: currentUser?.uid,
      invitatoDaNome: currentUser?.displayName || "",
      ruoloPrevisto,
      diventaTitolare, // se true, chi accetta nasce già Titolare — mai accesso in attesa
      stato: "in_attesa",
      createdAt: serverTimestamp(),
      scadenza,
    });
    return ref.id;
  }

  // Revoca un invito non ancora accettato — non elimina il documento (resta nella
  // cronologia degli inviti), semplicemente non sarà più utilizzabile.
  async function revocaInvito(invitoId) {
    await updateDoc(doc(db, "inviti", invitoId), { stato: "revocato" });
  }

  // Ingresso pubblico nella demo — accesso anonimo Firebase, nessuna registrazione. Riusa
  // la stessa identità anonima se il visitatore torna dallo stesso browser (comportamento
  // di default di Firebase), così non si accumula un documento nuovo ad ogni visita.
  async function entraNellaDemo() {
    const result = await signInAnonymously(auth);
    await setDoc(doc(db, "users", result.user.uid), {
      uid: result.user.uid,
      displayName: "Visitatore Demo",
      role: "titolare", // vede tutto, senza dover configurare abilitazioni per un profilo usa-e-getta
      ruoloOperativo: null,
      organizationId: DEMO_ORG_ID,
      enabled: true,
      isDemoVisitor: true,
      createdAt: serverTimestamp(),
    }, { merge: true });
    return result;
  }

  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profile = await fetchUserProfile(result.user.uid);

    // Platform owner bypass
    if (result.user.uid === PLATFORM_OWNER_UID) return result;

    if (!profile) throw new Error("Profilo utente non trovato.");
    if (!profile.enabled) {
      await signOut(auth);
      throw new Error("Account non ancora abilitato. Contatta l'amministratore.");
    }

    // Verifica organizzazione attiva
    if (profile.organizationId) {
      const orgDoc = await getDoc(doc(db, "organizations", profile.organizationId)).catch(() => null);
      if (orgDoc && orgDoc.exists() && orgDoc.data().status === "suspended") {
        await signOut(auth);
        throw new Error("Account sospeso. Contatta il supporto Nexiora.");
      }
    }

    return result;
  }

  async function logout() {
    await signOut(auth);
    setUserProfile(null);
    setOrganization(null);
  }

  // Recupero password (W447, soluzione A2): la Cloud Function richiediResetPassword fa generare il
  // link a Firebase e manda l'email Nexiora; il link apre src/pages/AzioneAccount.js. La risposta
  // è sempre la stessa, email registrata o no: chi chiama mostra sempre lo stesso messaggio.
  async function resetPassword(email) {
    try {
      await httpsCallable(getFunctions(), "richiediResetPassword")({ email: String(email || "").trim() });
    } catch (err) {
      // Solo l'email scritta male o troppi tentativi da questa connessione hanno un messaggio
      // diverso: non dicono nulla su chi è registrato.
      if (err?.code === "functions/invalid-argument") { const e = new Error(err.message); e.code = "auth/invalid-email"; throw e; }
      if (err?.code === "functions/resource-exhausted") { const e = new Error("Troppe richieste da questa connessione: riprova più tardi."); e.code = "auth/too-many-requests"; throw e; }
      // Qualunque altro esito resta silenzioso.
    }
  }

  // Cambio password da collegato (W447): prima si conferma quella attuale (riautenticazione),
  // così chi trova un computer lasciato aperto non può cambiarla.
  async function cambiaPassword(attuale, nuova) {
    const u = auth.currentUser;
    if (!u?.email) throw new Error("Nessun utente collegato.");
    await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, attuale));
    await updatePassword(u, nuova);
  }

  async function fetchUserProfile(uid) {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const profile = userDoc.data();
      setUserProfile(profile);

      // Carica organizzazione. Con le regole 72 chi è in attesa o disabilitato non la legge:
      // l'errore non deve bloccare il caricamento dell'app (si vedrà il messaggio di accesso).
      if (profile.organizationId) {
        try {
          const orgDoc = await getDoc(doc(db, "organizations", profile.organizationId));
          if (orgDoc.exists()) setOrganization({ id: orgDoc.id, ...orgDoc.data() });
        } catch { setOrganization(null); }
      }

      return profile;
    }
    return null;
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      setEmailVerificata(!!user?.emailVerified);
      // email confermata da un'altra scheda o da un altro dispositivo: l'utente salvato non lo sa ancora
      if (user && !user.emailVerified && !user.isAnonymous) await aggiornaVerificaEmail();
      if (user) await fetchUserProfile(user.uid);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Chi ha confermato l'email in un'altra scheda (link nell'email) torna qui: si ricontrolla
  // al rientro sulla pagina, finché non risulta confermata.
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous || emailVerificata) return undefined;
    const h = () => { if (document.visibilityState === "visible") aggiornaVerificaEmail(); };
    document.addEventListener("visibilitychange", h);
    window.addEventListener("focus", h);
    return () => { document.removeEventListener("visibilitychange", h); window.removeEventListener("focus", h); };
  }, [currentUser, emailVerificata]);

  const isPlatformOwner = currentUser?.uid === PLATFORM_OWNER_UID;
  const isOrgAdmin = userProfile?.role === "admin" || userProfile?.role === "titolare";
  const isTitolare = userProfile?.role === "titolare";
  const canSeeEconomic = isPlatformOwner || userProfile?.role === "titolare" || userProfile?.role === "admin";
  const organizationId = userProfile?.organizationId;

  // Rev2: nessun piano gratuito — "limitata" = prova finita senza piano pagato
  const isFreePlanLimited = statoPiano(organization, { isPlatformOwner }).bloccoTotale;

  // ── Logica prova e sola lettura (fonte unica: utils/pianiNexiora.js) ─────
  // • Prova attiva: tutti i moduli, Gold compreso.
  // • Prova finita senza piano pagato (Rev2, nessun piano gratuito): TUTTA l'organizzazione
  //   in sola lettura, dati consultabili ed esportabili, finché non si attiva un piano.
  // • Piano pagato = plan pro/gold con status "active" (impostato dal Platform Owner).
  const statoAbbonamento = statoPiano(organization, { isPlatformOwner });
  const isTrialAttivo = !isPlatformOwner && statoAbbonamento.inProva;
  const isTrialScaduto = !isPlatformOwner && statoAbbonamento.provaScaduta;
  const isReadOnly = !isPlatformOwner && statoAbbonamento.bloccoTotale;
  const puoModificareTaskSottoattivita = isPlatformOwner || !isReadOnly;
  const giorniTrialRimanenti = isPlatformOwner ? null : statoAbbonamento.giorniProva;

  const value = {
    currentUser, userProfile, organization, organizationId, isTitolare, canSeeEconomic,
    isPlatformOwner, isOrgAdmin, isFreePlanLimited,
    isTrialAttivo, isTrialScaduto, isReadOnly, puoModificareTaskSottoattivita, giorniTrialRimanenti, statoAbbonamento,
    login, logout, registerOrganization, registerMember, entraNellaDemo, fetchUserProfile,
    creaInvito, revocaInvito, resetPassword, cambiaPassword,
    verificaPivaRegistrazione, richiediAccesso, emailVerificata, aggiornaVerificaEmail, rimandaVerificaEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
